<template>
	<div v-if="dashboards.dashboard" class="bar" :class="{'visible':dashboards.editMode}">
		<label v-if="dashboards.mode === DashboardMode.CREATE">{{ t('actionbar.newLayoutName') }}</label>
		<label v-if="dashboards.mode === DashboardMode.EDIT">{{ t('actionbar.layoutName') }}</label>
		<InputText id="DashboardName" size="small" maxlength="64" class="max-w-60 min-w-12 mr-2" v-model="dashboards.dashboard.name" />

		<label class="ml-2">{{ t('actionbar.useLayoutInTopbar') }}</label>
		<Select class="w-44 mr-5" v-model="dashboards.dashboard.topbar" :options="topbarPriorities" optionValue="value" optionLabel="label" size="small" append-to="self" />

		<label v-if="ss.auth?.accessLevel !== EmployeeAccessLevel.PERSONAL && ss.auth?.accessLevel !== EmployeeAccessLevel.VIEWER" class="ml-2">{{ t('actionbar.availability') }}</label>
		<Select v-if="ss.auth?.accessLevel !== EmployeeAccessLevel.PERSONAL && ss.auth?.accessLevel !== EmployeeAccessLevel.VIEWER" class="w-32 mr-5" v-model="dashboards.dashboard.shared"
			:options="sharedOptions" optionValue="value" optionLabel="label" size="small" :disabled="dashboards.mode !== DashboardMode.CREATE" append-to="self" />

		<label v-if="ss.auth?.accessLevel !== EmployeeAccessLevel.PERSONAL && ss.auth?.accessLevel !== EmployeeAccessLevel.VIEWER && dashboards.dashboard.shared" class="ml-2">{{ t('actionbar.forMembersOf') }}</label>
		<Select v-if="ss.auth?.accessLevel !== EmployeeAccessLevel.PERSONAL && ss.auth?.accessLevel !== EmployeeAccessLevel.VIEWER && dashboards.dashboard.shared" class="w-44 mr-5"
			v-model="dashboards.dashboard.group" :options="dashboards.pbxGroups" optionValue="_id" optionLabel="text" size="small" append-to="self" />

		<Button :disabled="dashboards.dashboard.name.length === 0 || dashboards.dashboard.items.length === 0" size="small" @click="dashboards.save()">{{ dashboards.mode === DashboardMode.CREATE ? t('common.create') : t('common.save') }}</Button>
		<Button size="small" severity="danger" :disabled="!dashboards.dashboard" @click="remove()">{{ t('common.delete') }}</Button>
		<Button size="small" severity="secondary" @click="dashboards.cancel()">{{ t('common.cancel') }}</Button>
	</div>
</template>

<script setup lang="ts">
import { useSettingsStore } from '@/stores/Settings'
import globalEvents from '@/utilities/GlobalEvents'
import { useDashboardsStore } from '@/stores/Dashboards'
import { useI18n } from 'vue-i18n'
import { ref, watch } from 'vue'
import { DashboardMode } from '@/stores/Dashboards.types'
import { EmployeeAccessLevel } from '@per_moeller/tcx-realtime-client'

const ss = useSettingsStore()
const dashboards = useDashboardsStore()
const { t } = useI18n()

const topbarPriorities = ref<{ value: number, label: string }[]>([
	{ value: 0, label: t('common.no') },
	{ value: 1, label: t('actionbar.topbarPriority') + ' 1' },
	{ value: 2, label: t('actionbar.topbarPriority') + ' 2' },
	{ value: 3, label: t('actionbar.topbarPriority') + ' 3' },
	{ value: 4, label: t('actionbar.topbarPriority') + ' 4' },
	{ value: 5, label: t('actionbar.topbarPriority') + ' 5' }
])
const sharedOptions = ref<{ index: number, label: string, value: boolean }[]>([
	{ index: 0, label: t('actionbar.personal'), value: false },
	{ index: 1, label: t('actionbar.shared'), value: true }
])

function remove() {
	if (dashboards.dashboard) {
		globalEvents.emit('CommonQuestionModal', { header: t('actionbar.deleteHeader'), message: `${t('actionbar.deleteMessage')} ${dashboards.dashboard.name}?`, ok: t('common.delete'), emit: 'Dashboard:Delete', emitData: dashboards.dashboard._id })
	}
}

watch(() => dashboards.editMode, (newValue: boolean) => {
	if (newValue) {
		setTimeout(() => {
			const input = document.getElementById('DashboardName') as HTMLInputElement

			if (input) { input.focus() }
		}, 200)
	}
})
</script>

<style scoped>
	.bar {
		background: var(--p-surface-200);
		box-shadow: 0px 1px 2px 0px rgba(0,0,0,0.25);
		height: 40px;
		padding-left: 10px;
		padding-right: 10px;
		padding-top: 3px;
		display: flex;
		align-items: center;
		position: fixed;
		left: 0;
		top: 73px;
		right: 0;
		transition: 500ms;
		margin-top: -45px;
		z-index: 99;
	}
	.bar.macos {
		top: 90px;
	}
	.bar.visible {
		margin-top: 0px;
	}
	.bar LABEL {
		margin-bottom: 0;
		font-size: var(--font-size-secondary);
		font-weight: 300;
		padding-right: 5px;
	}
	.bar BUTTON {
		margin-top: 0;
		margin-bottom: 0;
		width: 100px;
		margin-right: 10px;
	}
	@media (prefers-color-scheme: dark) {
		.bar {
			background: var(--p-surface-800);
			box-shadow: 0px 1px 2px 0px rgba(0,0,0,0.5);
		}
	}
</style>
